import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify, useRecordContext, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { fullDateTime, shortDateTime } from '../../shared/dateTime';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import {
  fetchOrderPublications,
  requestPublication,
  maskingModeLabel,
  noAccessText,
  testModeText,
  subcontractorRuleSentence,
  ENVIRONMENT_PRODUCTION,
  EXCHANGE_TIMOCOM,
  EXCHANGE_LABELS,
  PUBLISHING_EXCHANGES,
  accountSentence,
  maskingModesFor,
  fleetVariantsWarningText,
  FEATURE_PUBLICATION_DATA_CONFIRMATION,
  dataConfirmationLabel,
  publicationDataRows,
  sameVehicle,
  type PostingEligibility,
  type Publication,
} from './publicationsApi';
import { PublicationExceptionSection } from './PublicationExceptionSection';
import { FEATURE_PUBLICATION_EXCEPTIONS } from './publicationExceptionsApi';
import { OfferPreview } from './OfferPreview';
import { VehicleChoice, draftFromProfile, profileFromDraft, type VehicleDraft } from './VehicleChoice';
import { PublicationActions } from './PublicationActions';
import { ExchangeName, PostingStatus, PublicationOutcome } from './PublicationRow';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { recordStateKey } from '../../shared/recordStateKey';
import { useHasPermission } from '../PermissionGate';
import { useFeature } from '../featureFlags/FeatureGate';
import { ContractTestId } from '../../usage/ContractTestId';

/** Spółka zlecenia nie ma danych dostępowych do tej giełdy — wiadomo z API. */
const lacksAccess = (m: PostingEligibility | null) => m?.accessConfigured === false;

/**
 * Trans.eu: przez czyje konto przewoźnicy się kontaktują (16.10.2026) —
 * konto imienne tradera, konto wspólne innej spółki albo konto spółki.
 */
const transeuContactText = (m: PostingEligibility | null) => {
  if (m?.account?.kind === 'personal') return 'Przewoźnicy będą się kontaktować przez Twoje konto Trans.eu.';
  if (m?.account?.kind === 'shared' && m.account.companyName)
    return `Przewoźnicy będą się kontaktować przez konto Trans.eu spółki ${m.account.companyName}.`;
  return 'Przewoźnicy będą się kontaktować przez konto Trans.eu spółki.';
};

/** Dane dostępowe są, ale instancja rozmawia ze środowiskiem testowym giełdy. */
const inTestMode = (m: PostingEligibility | null) =>
  m?.accessConfigured === true && m.environment !== ENVIRONMENT_PRODUCTION;

/**
 * Publikacje zlecenia na giełdach — sekcja na ekranie zlecenia.
 *
 * ## Przycisk, który mówi, czemu nie
 *
 * Czy zlecenie da się wystawić, rozstrzyga serwer (dział Trade, brak
 * własnego auta, komplet poświadczeń spółki, brak żywej publikacji, zakaz
 * klienta bez uprawnienia do wyjątku). Sekcja nie ma własnej kopii tych
 * reguł: pokazuje przycisk wyłączony z powodem z odpowiedzi.
 *
 * ## Dwie bramki w oknie potwierdzenia
 *
 * Serwer mówi, o co zapytać człowieka, a okno pyta:
 * - **dopuszczalność podwykonawcy** — reguła nieustalona albo warunkowa
 *   wymaga zaznaczenia potwierdzenia; zakaz wymaga jawnego wyjątku
 *   z powodem (tylko uprawniona osoba dostaje ten przycisk);
 * - **maskowanie lokalizacji** — gdy spółka nie ma ustawionego trybu,
 *   operator wybiera go dla tej publikacji; gdy ma, widzi który.
 * Bez wymaganych decyzji przycisk „Wystaw" jest wyłączony — serwer i tak by
 * odmówił, ale odmowa po kliknięciu to zła forma pytania.
 *
 * ## Pojazd podpowiedziany z typu zlecenia (01.10.2026)
 *
 * Okno wstawia profil pojazdu z podpowiedzi serwera (typ zlecenia, bez typu
 * — JUMBO, bo Marathon ma wyłącznie JUMBO TRUCK) i podpisuje jej źródło.
 * Trader może go zmienić; typ spoza słownika nie ma podpowiedzi i wtedy
 * „Wystaw” czeka na wybór. Niczego nie wystawiamy bez kliknięcia.
 *
 * ## Brak dostępu i tryb testowy (omówienie 01.10.2026)
 *
 * Gdy spółka zlecenia nie ma danych dostępowych do giełdy, przy przycisku
 * stoi jeden komunikat: „Brak dostępu do giełdy … — wystawianie nieaktywne
 * do czasu otrzymania konta.” — zamiast baneru o środowisku i powodu
 * odmowy. Baner trybu testowego jest tylko wtedy, gdy dane dostępowe są,
 * a instancja rozmawia ze środowiskiem testowym giełdy. Wcześniejszy baner
 * „Instancja gada z piaskownicą TIMOCOM” mylił: dostępu nie było żadnego.
 *
 * ## Odświeżanie po zmianie stanu zlecenia
 *
 * Dane ładują się od nowa przy każdej zmianie stanu zlecenia
 * (`recordStateKey`), nie tylko przy zmianie rekordu — po przekazaniu do
 * Trade sekcja pokazywała dalej „nie jest przekazane do Trade”.
 *
 * ## Przycisk na giełdę
 *
 * Od 24.09.2026 dwie giełdy: TIMOCOM i Trans.eu. Każda ma własny przycisk
 * z własnym powodem odmowy (inne poświadczenia, inna żywa publikacja);
 * bramki i okno potwierdzenia są wspólne.
 *
 * ## Planista widzi sam status (01.10.2026)
 *
 * Rozdział FTL §5: „status publikacji i sprzedaży ma być widoczny bez pytania
 * innego działu”; §14: „FTL widzi giełdy i status, nie musi pytać Trade”.
 * Bez uprawnienia Trade sekcja pokazuje tabelę publikacji i tryb testowy,
 * a przyciski wystawienia, wycofania i ponowienia oraz regułę klienta
 * ukrywa (nie wyszarza) — serwer i tak odmówiłby 403.
 *
 * ## Konto oferty i tryb „przybliżony” (odpowiedzi 16.10.2026)
 *
 * Za przełącznikami kont (`exchangeBothCompanies`, `exchangePersonalAccount`)
 * serwer mówi, z którego konta pójdzie oferta — sekcja pokazuje to zdaniem
 * przy przycisku i w oknie. Brak konta imiennego tradera ma własny powód
 * z serwera zamiast ogólnego „brak dostępu”. Tryb „przybliżony” jest do
 * wyboru tylko z przełącznikiem `maskingNearbyLocality`.
 *
 * ## Wniosek o wyjątek i warianty floty (fala B, 21.10.2026)
 *
 * Z przełącznikiem `publicationExceptionRequests` sekcja pokazuje wniosek
 * o wyjątek od bramki (`PublicationExceptionSection`): trader prosi
 * o wyjątek, osoba uprawniona zatwierdza, a okno publikacji przy
 * zatwierdzonym wniosku nie pyta o potwierdzenie ani wyjątek. Z przełącznikiem
 * `publicationFleetVariantsWarning` serwer podaje warianty floty zlecenia —
 * sekcja i okno pokazują ostrzeżenie z autorami (nie blokadę), a zlecenie
 * publikacji niesie informację, że ostrzeżenie było widoczne.
 *
 * ## Potwierdzenie danych do publikacji (paczka p. Mariusza 21.10, S-21.10-19)
 *
 * Z przełącznikiem `publicationDataConfirmation` okno ma krok „Potwierdzam
 * dane do publikacji” (BR-TRD-12): typ pojazdu i zabudowa, długość, wysokość
 * (TMS jej nie podaje) i wózek. Wartości z typu zlecenia są podpowiedzią do
 * potwierdzenia, nie wartością domyślną — bez zaznaczenia „Wystaw” jest
 * nieaktywny, a serwer i tak odmówi. Identyfikatory z kontraktu makiety:
 * `trd-zlecenie-dane-potwierdz`, `trd-publikacja-bramka`, `trd-publikacja-publikuj`.
 */
const ExchangePublicationsPanel = () => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const canTrade = useHasPermission('TRADE');
  const nearbyOn = useFeature('maskingNearbyLocality');
  const exceptionsOn = useFeature(FEATURE_PUBLICATION_EXCEPTIONS);
  const dataConfirmOn = useFeature(FEATURE_PUBLICATION_DATA_CONFIRMATION);
  const [dataConfirmed, setDataConfirmed] = useState(false);
  const [publications, setPublications] = useState<Publication[] | null>(null);
  const [eligibilityByExchange, setEligibilityByExchange] = useState<
    Record<string, PostingEligibility>
  >({});
  /** Giełda, dla której otwarte jest okno potwierdzenia; `null` = zamknięte. */
  const [dialogExchange, setDialogExchange] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [exception, setException] = useState(false);
  const [mode, setMode] = useState('');
  /** Pojazd w edycji; `null` = brak podpowiedzi i jeszcze nie wybrany. */
  const [vehicleDraft, setVehicleDraft] = useState<VehicleDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const stillMounted = useStillMounted();
  const id = record?.id;
  const stateKey = recordStateKey(record);
  // Numer ostatniego odczytu — starsza odpowiedź nie nadpisze nowszej.
  const lastLoad = useRef(0);

  const load = useCallback(async () => {
    if (id === undefined) return;
    const call = ++lastLoad.current;
    try {
      const response = await fetchOrderPublications(id);
      if (!stillMounted() || call !== lastLoad.current) return;
      setPublications(response.postings);
      setEligibilityByExchange(
        response.canPublishByExchange ?? { [EXCHANGE_TIMOCOM]: response.canPublish },
      );
    } catch (e) {
      if (!stillMounted() || call !== lastLoad.current) return;
      notify(toErrorMessage(e), { type: 'error' });
    }
  }, [id, notify, stillMounted]);

  // Stan zlecenia w zależnościach celowo: `load` się nie zmienia, a odmowy
  // (dział Trade, obsadzenie) zależą od stanu, który właśnie się zmienił.
  useEffect(() => {
    void load();
  }, [load, stateKey]);

  if (!record) return null;

  const timocom = eligibilityByExchange[EXCHANGE_TIMOCOM] ?? null;
  const canPublish = eligibilityByExchange[dialogExchange ?? EXCHANGE_TIMOCOM] ?? timocom;
  const dialogOpen = dialogExchange !== null;
  const exchangeLabel = EXCHANGE_LABELS[dialogExchange ?? EXCHANGE_TIMOCOM];
  const gates = canPublish?.bramki ?? null;
  const eligibility = gates?.dopuszczalnosc;
  const mask = gates?.maskowanie;
  const needsMode = mask?.requiresChoice === true;
  // Zatwierdzony wniosek o wyjątek (fala B) rozstrzyga bramkę za tradera.
  const approvedRequest =
    eligibility?.exceptionRequest?.status === 'approved' ? eligibility.exceptionRequest : null;
  const needsException = eligibility?.requiresException === true && !approvedRequest;
  const needsConfirmation = eligibility?.requiresConfirmation === true && !approvedRequest;
  // Warianty floty — ta sama lista przy każdej giełdzie (zlecenie, nie oferta).
  const fleetVariants =
    Object.values(eligibilityByExchange).find((m) => (m?.fleetVariants?.length ?? 0) > 0)?.fleetVariants ?? [];
  const vehicleSuggestion = canPublish?.vehicle ?? null;
  const vehicleProfile = vehicleDraft ? profileFromDraft(vehicleDraft) : null;
  const vehicleMissing = vehicleSuggestion !== null && vehicleProfile === null;
  const missingDecision =
    vehicleMissing ||
    (needsMode && !mode) ||
    (needsException && (!exception || !reason.trim())) ||
    (needsConfirmation && !confirmed) ||
    (dataConfirmOn && !dataConfirmed);

  const close = () => {
    setDialogExchange(null);
    setReason('');
    setConfirmed(false);
    setException(false);
    setMode('');
    setVehicleDraft(null);
    setDataConfirmed(false);
  };

  const openDialog = (exchange: string) => {
    setVehicleDraft(draftFromProfile(eligibilityByExchange[exchange]?.vehicle?.profile ?? null));
    setDialogExchange(exchange);
  };

  const publish = async () => {
    setBusy(true);
    try {
      await requestPublication(record.id, dialogExchange ?? EXCHANGE_TIMOCOM, {
        reason,
        eligibilityConfirmed: needsConfirmation ? confirmed : undefined,
        banException: needsException ? exception : undefined,
        maskingMode: needsMode ? mode : undefined,
        vehicle: vehicleProfile ?? undefined,
        // Okno pokazało ostrzeżenie o wariantach floty — zapis w wyniku.
        fleetVariantsShown: fleetVariants.length > 0,
        // Potwierdzenie danych do publikacji (S-21.10-19) — tylko z przełącznikiem.
        dataConfirmed: dataConfirmOn ? dataConfirmed : undefined,
      });
      // Bez obietnicy wystawienia (omówienie 01.10.2026) — giełda może odmówić.
      notify('Zlecono publikację — system spróbuje wystawić ofertę w ciągu minuty.', { type: 'success' });
      close();
      await load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const notifyUser = (message: string, kind: 'success' | 'error' | 'warning') => notify(message, { type: kind });
  // TIMOCOM zawsze (sekcja sprzed Trans.eu), pozostałe — gdy API je podało.
  const exchanges = PUBLISHING_EXCHANGES.filter(
    (x) => x === EXCHANGE_TIMOCOM || eligibilityByExchange[x],
  );

  return (
    <SectionCard title={`Publikacje na giełdach${publications ? ` (${publications.length})` : ''}`}>
      {/* Środowisko testowe ma dziś tylko TIMOCOM; Trans.eu to zawsze
          prawdziwa giełda — reguła siedzi w API (`environment`). */}
      {exchanges
        .filter((x) => inTestMode(eligibilityByExchange[x] ?? null))
        .map((x) => (
          <Alert key={x} severity="warning" sx={{ mb: 1 }}>
            {testModeText(x)}
          </Alert>
        ))}

      {/* Reguła klienta i lokalizacja na giełdzie zdaniami (01.10.2026). */}
      {canTrade && eligibility && (
        <Typography variant="caption" color={eligibility.regula === 'zakaz' ? 'error.main' : 'text.secondary'} sx={{ display: 'block', mb: 0.5 }}>
          {subcontractorRuleSentence(eligibility.regula, eligibility.contractor?.legalName)}
          {mask &&
            (mask.requiresChoice
              ? ' Spółka nie ma ustawionego trybu maskowania lokalizacji — wybierzesz go przy publikacji.'
              : ` Lokalizacja na giełdzie: ${maskingModeLabel(mask.companyMode)}.`)}
        </Typography>
      )}
      {/* Typ klienta z TMS (16.10.2026, pyt. 38) — podpowiedź obok reguły,
          niczego nie odblokowuje (reguła dalej wpisywana przez człowieka). */}
      {canTrade && eligibility?.customerTypeHint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {eligibility.customerTypeHint}
        </Typography>
      )}

      {/* Warianty floty (fala B, D6) — ostrzeżenie, nie blokada. */}
      {canTrade && fleetVariants.length > 0 && (
        // Kontrakt `data-testid` paczki (S-22.10-02): „konsultacja z FTL” — u nas ostrzeżenie, nie krok.
        <ContractTestId id="trd-zlecenie-konsultacja-ftl" block>
          <Alert severity="warning" sx={{ mb: 1 }} data-testid="fleet-variants-warning">
            {fleetVariantsWarningText(fleetVariants)}
          </Alert>
        </ContractTestId>
      )}

      {/* Wniosek o wyjątek od bramki (fala B) — stan widzi każdy, kto widzi sekcję. */}
      {exceptionsOn && (
        <PublicationExceptionSection
          userOfferId={record.id}
          eligibility={eligibility ?? null}
          canTrade={canTrade}
          onChanged={() => void load()}
        />
      )}

      {canTrade && exchanges.map((x) => {
        const m = eligibilityByExchange[x] ?? null;
        const noAccess = lacksAccess(m);
        // Bez dostępu do giełdy jeden komunikat — inne powody odmowy nic
        // tu nie zmienią, dopóki spółka nie dostanie konta.
        // Brak konta imiennego (16.10.2026) — powód z serwera, nie „brak dostępu”.
        const blocked = noAccess
          ? (m?.accessReason ?? noAccessText(x))
          : m && !m.allowed
            ? (m.reason ?? '')
            : '';
        const account = !blocked ? accountSentence(m?.account) : null;
        return (
          <Stack key={x} direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
            <Tooltip title={blocked}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!m?.allowed || noAccess || busy}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => openDialog(x)}
                  data-testid="trd-zlecenie-do-publikacji"
                  data-tip={blocked || undefined}
                >
                  Wystaw na {EXCHANGE_LABELS[x]}
                </Button>
              </span>
            </Tooltip>
            {blocked && (
              <Typography variant="caption" color="text.secondary">
                {blocked}
              </Typography>
            )}
            {account && (
              <Typography variant="caption" color="text.secondary">
                {account}
              </Typography>
            )}
          </Stack>
        );
      })}

      {publications && publications.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Zlecenie nie było jeszcze wystawiane na giełdach.
        </Typography>
      )}

      {publications && publications.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Giełda</TableCell>
              <TableCell>#</TableCell>
              <TableCell>Stan</TableCell>
              <TableCell>Zlecił</TableCell>
              <TableCell>Zlecono</TableCell>
              <TableCell>Wystawiono</TableCell>
              <TableCell>Wynik</TableCell>
              {canTrade && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {publications.map((p) => (
              <TableRow key={p.id}>
                <TableCell sx={{ border: 0 }}>
                  <ExchangeName exchange={p.exchange} />
                </TableCell>
                <TableCell sx={{ border: 0 }}>{p.postingNumber}</TableCell>
                <TableCell sx={{ border: 0 }}>
                  <PostingStatus status={p.status} />
                </TableCell>
                <TableCell sx={{ border: 0 }}>{p.requestedBy?.username ?? '—'}</TableCell>
                {/* Bez sekund w komórce, pełny zapis w dymku. */}
                <TableCell sx={{ border: 0, whiteSpace: 'nowrap' }}>
                  <Tooltip title={fullDateTime(p.requestedAt)}>
                    <span>{shortDateTime(p.requestedAt)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ border: 0, whiteSpace: 'nowrap' }}>
                  <Tooltip title={p.postedAt ? fullDateTime(p.postedAt) : ''}>
                    <span>{shortDateTime(p.postedAt)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ border: 0 }}>
                  <PublicationOutcome publication={p} />
                </TableCell>
                {canTrade && (
                  <TableCell sx={{ border: 0 }} align="right">
                    <PublicationActions publication={p} onChanged={load} notify={notifyUser} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={canTrade && dialogOpen} onClose={() => !busy && close()} fullWidth maxWidth="sm">
        <DialogTitle>Wystawić zlecenie na {exchangeLabel}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Na giełdę trafi oferta frachtu: punkty trasy, terminy, masa i długość ładunku —{' '}
            <strong>bez ceny</strong> i bez uwag wewnętrznych.{' '}
            {dialogExchange === EXCHANGE_TIMOCOM
              ? 'Osobą kontaktową będziesz Ty (dane z Twojego profilu).'
              : transeuContactText(canPublish)}{' '}
            Po kliknięciu „Wystaw” system spróbuje wystawić ofertę w ciągu minuty — wynik i przyjęte
            założenia zobaczysz w tej sekcji.
          </Typography>
          {accountSentence(canPublish?.account) && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {accountSentence(canPublish?.account)}
            </Typography>
          )}

          {fleetVariants.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }} data-testid="publish-dialog-fleet-variants">
              {fleetVariantsWarningText(fleetVariants)} Zlecenie publikacji zapisze, że ostrzeżenie było widoczne.
            </Alert>
          )}

          {approvedRequest && (
            <Alert severity="success" sx={{ mb: 1 }} data-testid="publish-dialog-approved-exception">
              Wyjątek od reguły klienta zatwierdzony na wniosek nr {approvedRequest.id} (zatwierdził{' '}
              {approvedRequest.decidedBy?.username ?? '—'}) — potwierdzenie nie jest potrzebne; numer wniosku zapisze
              się w historii zlecenia.
            </Alert>
          )}

          {eligibility && needsException && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Klient <strong>{eligibility.contractor?.legalName}</strong> zakazuje przekazywania zleceń
              podwykonawcom. Publikacja jest możliwa tylko jako wyjątek udzielony przez uprawnioną
              osobę, z podaniem powodu. System zapisze, kto, kiedy i dlaczego go udzielił.
            </Alert>
          )}
          {eligibility && needsException && (
            <FormControlLabel
              control={<Checkbox checked={exception} onChange={(e) => setException(e.target.checked)} />}
              label="Udzielam wyjątku od zakazu podwykonawstwa dla tego zlecenia"
            />
          )}

          {eligibility && needsConfirmation && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {eligibility.regula === 'nieustalona'
                ? 'Nie ustalono, czy ten klient dopuszcza podwykonawców. Dopóki reguła nie zostanie wpisana w kartotece kontrahenta, publikacja wymaga ręcznego potwierdzenia.'
                : 'Klient dopuszcza podwykonawców warunkowo — potwierdź, że warunki są spełnione.'}
            </Alert>
          )}
          {eligibility && needsConfirmation && (
            <FormControlLabel
              data-testid="trd-publikacja-bramka"
              control={
                <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              }
              label={
                eligibility.contractor?.legalName
                  ? `Potwierdzam, że dla klienta ${eligibility.contractor.legalName} można zlecić przewóz podwykonawcy`
                  : 'Potwierdzam, że to zlecenie można przekazać podwykonawcy'
              }
            />
          )}

          {mask && needsMode && (
            <FormControl size="small" fullWidth sx={{ mt: 1, mb: 1 }}>
              <InputLabel id="tryb-maskowania">Lokalizacja na giełdzie (spółka nie ma ustawionego trybu)</InputLabel>
              <Select
                labelId="tryb-maskowania"
                label="Lokalizacja na giełdzie (spółka nie ma ustawionego trybu)"
                value={mode}
                onChange={(e) => setMode(String(e.target.value))}
              >
                {maskingModesFor(nearbyOn).map((t) => (
                  <MenuItem key={t.kod} value={t.kod}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {mask && !needsMode && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Lokalizacja na giełdzie: {maskingModeLabel(mask.companyMode)} — zgodnie z ustawieniem spółki.
            </Typography>
          )}

          {vehicleSuggestion && (
            <VehicleChoice suggestion={vehicleSuggestion} value={vehicleDraft} onChange={setVehicleDraft} />
          )}

          {/* Zanim ktoś kliknie „Wystaw", ma zobaczyć, co wystawia. Zmiana
              trybu albo pojazdu unieważnia pokazany podgląd (nowy klucz). */}
          <OfferPreview
            key={`${mode}|${JSON.stringify(vehicleProfile)}`}
            userOfferId={record.id}
            mode={needsMode ? mode : undefined}
            exchange={dialogExchange ?? EXCHANGE_TIMOCOM}
            vehicle={vehicleProfile ?? undefined}
          />

          {dataConfirmOn && (
            <Box sx={{ mt: 1 }} data-testid="publication-data-confirmation">
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Dane do publikacji — sprawdź i potwierdź
              </Typography>
              <Table size="small" aria-label="Dane do publikacji">
                <TableBody>
                  {publicationDataRows(
                    vehicleProfile,
                    sameVehicle(vehicleProfile, vehicleSuggestion?.profile ?? null),
                    vehicleSuggestion?.source,
                  ).map((row) => (
                    <TableRow key={row.label}>
                      <TableCell sx={{ border: 0, py: 0.25, pl: 0, whiteSpace: 'nowrap' }}>{row.label}</TableCell>
                      <TableCell sx={{ border: 0, py: 0.25 }}>{row.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <FormControlLabel
                data-testid="trd-zlecenie-dane-potwierdz"
                control={<Checkbox checked={dataConfirmed} onChange={(e) => setDataConfirmed(e.target.checked)} />}
                label={dataConfirmationLabel(vehicleSuggestion?.source)}
              />
            </Box>
          )}

          <TextField
            label={needsException ? 'Powód wyjątku (wymagany, zapisze się w historii zlecenia)' : 'Powód (opcjonalnie, zapisze się w historii zlecenia)'}
            fullWidth
            size="small"
            required={needsException}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={() => void publish()}
            disabled={busy || missingDecision}
            data-testid="trd-publikacja-publikuj"
          >
            Wystaw
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const ExchangePublications = recordScoped(ExchangePublicationsPanel);
